import * as React from "react";
import {Result, Spin, Tooltip, Typography} from "antd";
import {addSeconds, format, formatDuration, parseISO} from "date-fns";

export const withTooltip = (title: string, key: string, Element: any) => {

    return (
        <Tooltip title={title} key={key}>
            {Element}
        </Tooltip>
    )
};

/**
 * Hook that alerts clicks outside of the passed ref
 */
export function useOutsideAlerter(ref: any, onClickAway: (event: Event) => void, listening: boolean) {
    React.useEffect(() => {
        /**
         * Alert if clicked on outside of element
         */
        function handleClickOutside(event: any) {
            if (!listening) {
                return;
            }
            if (ref.current && !ref.current.contains(event.target)) {
                event.stopPropagation();
                event.preventDefault();
                onClickAway(event);
                return;
            }
            if (ref instanceof HTMLElement && !ref.contains(event.target)) {
                event.stopPropagation();
                event.preventDefault();
                onClickAway(event);
                return;
            }
        }

        // Bind the event listener
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref, listening, onClickAway]);
}

export const dummyRequest: (options: any) => void = (options) => {
    const {onSuccess} = options;
    setTimeout(() => {
        onSuccess({ok: true});
    }, 200);
};

interface UseDynamicSVGImportOptions {
    onCompleted?: (
        name: string,
        SvgIcon: React.FC<React.SVGProps<SVGSVGElement>> | undefined
    ) => void;
    onError?: (err: Error) => void;
}

function useDynamicSVGImport(
    name: string,
    options: UseDynamicSVGImportOptions = {}
) {
    const ImportedIconRef = React.useRef<React.FC<React.SVGProps<SVGSVGElement>>>();
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<Error>();

    const {onCompleted, onError} = options;
    React.useEffect(() => {
        setLoading(true);
        const importIcon = async (): Promise<void> => {
            try {
                const fileName = name.split('.')[0];
                const imported = await import(`./svg-icons/${fileName}.svg`);
                ImportedIconRef.current = imported.ReactComponent;
                onCompleted?.(name, ImportedIconRef.current);
            } catch (err: any) {
                onError?.(err);
                setError(err);
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        importIcon();
    }, [name, onCompleted, onError]);

    return {error, loading, SvgIcon: ImportedIconRef.current as React.FunctionComponent};
}


export const DynamicIcon: React.FunctionComponent<{ name: string, svgProps: React.SVGProps<React.ReactSVGElement> }> = (
    {
        name,
        svgProps
    }
) => {
    const {error, loading, SvgIcon} = useDynamicSVGImport(name);

    if (loading) {
        return <Spin spinning={true}/>
    }

    if (error) {
        return <Result status="error" title={error.message}/>
    }

    return <SvgIcon {...svgProps}/>
}


export const Timer: React.FunctionComponent<{ seconds: number, onFinish: () => void }> = ({seconds, onFinish}) => {
    const now = new Date();
    const [target] = React.useState(addSeconds(now, seconds));
    const [count, setCount] = React.useState<number>(target.getTime() - (new Date().getTime()));

    React.useEffect(() => {
        let countdown: any = null;
        if (count <= 0) {
            onFinish();
            return;
        }

        countdown = setInterval(() => setCount(target.getTime() - (new Date().getTime())), 1000);

        return () => {
            if (countdown) {
                clearInterval(countdown);
            }
        }
    }, [target, count]);

    return (
        <Typography.Text style={{color: "inherit"}}>{format(new Date(count), "mm:ss")}</Typography.Text>
    )
}

export function fallbackCopyTextToClipboard(text: string) {
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
        document.execCommand('copy');
    } catch (err) {
        console.error('Fallback: Oops, unable to copy', err);
    }

    document.body.removeChild(textArea);
}

export function copyTextToClipboard(text: string, cb?: (text: string) => void) {
    if (!navigator.clipboard) {
        fallbackCopyTextToClipboard(text);
        if (!cb) return
        cb(text);
        return;
    }
    navigator.clipboard.writeText(text).then(function () {
        if (!cb) return
        cb(text);
    }, function (err) {
        console.error('Async: Could not copy text: ', err);
    });
}


export function formatMillisecondsToMinutesSeconds(ms: number) {
    // Convert milliseconds to seconds
    let totalSeconds = ms / 1000;

    // Calculate minutes and seconds
    let minutes = Math.floor(totalSeconds / 60);
    let seconds = Math.floor(totalSeconds % 60);

    // Return formatted string
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}


export function formatMillisecondsToHourMinutesSeconds(ms: number) {
    // Convert milliseconds to seconds
    let totalSeconds = ms / 1000;

    // Calculate hours, minutes, and seconds
    let hours = Math.floor(totalSeconds / 3600);
    let minutes = Math.floor((totalSeconds % 3600) / 60);
    let seconds = Math.floor(totalSeconds % 60);

    // Return formatted string
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}


export function humanizeSeconds(totalSeconds: number) {
    // Convert milliseconds to seconds

    // Calculate hours, minutes, and seconds
    let days = Math.floor(totalSeconds / 86400);
    let hours = Math.floor((totalSeconds % 86400) / 3600);
    let minutes = Math.floor((totalSeconds % 3600) / 60);
    let seconds = Math.floor(totalSeconds % 60);

    return formatDuration({
        days: days > 0 ? days : undefined,
        hours: hours > 0 ? hours : undefined,
        minutes: minutes > 0 ? minutes : undefined,
        seconds
    })

    // Return formatted string
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}


export const usePrevious: <T>(value: T) => T | null = (value) => {
    // The ref object is a generic container whose current property is mutable ...
    // ... and can hold any value, similar to an instance property on a class
    const ref = React.useRef<any | null>(null);
    // Store current value in ref
    React.useEffect(() => {
        ref.current = value;
    }, [value]); // Only re-run if value changes
    // Return previous value (happens before update in useEffect above)
    return ref.current;
}


export const useChatScroller: (deps: any[], offset: number) => {
    setScrollElement: React.Dispatch<React.SetStateAction<HTMLElement | null>>,
    scrollBottom: (force?: boolean) => void,
    scrollElement: HTMLElement | null,
    scrollNextUpdate: () => void
} = (deps, offset) => {
    const [element, setElement] = React.useState<HTMLElement | null>(null);
    const [pendingScroll, setPendingScroll] = React.useState(false);

    const scrollBottom = React.useCallback((force?: boolean) => {
        if ((!element || !pendingScroll) && (!element || !force)) return;
        element.scroll({top: element.scrollHeight + offset, left: 0, behavior: 'smooth'});
        setPendingScroll(false);
    }, [element, offset, pendingScroll])

    React.useEffect(() => {
        scrollBottom()
    }, [...deps, element, scrollBottom]);

    const scrollNextUpdate = React.useCallback(() => {
        setPendingScroll(true);
    }, [])

    return {
        setScrollElement: setElement,
        scrollBottom,
        scrollElement: element,
        scrollNextUpdate
    };
}


export function autoLinkUrls(html: string): string {
    // Create a new DOMParser
    const parser = new DOMParser();

    // Parse the HTML
    const doc = parser.parseFromString(html, 'text/html');

    // Regular expression to match URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    // Recursively search through each node in the DOM
    function searchNode(node: ChildNode) {
        if (node.nodeType === Node.TEXT_NODE && node.textContent) {
            // If the node is a text node, check if it contains a URL
            const matches = node.textContent.match(urlRegex);

            if (matches) {
                if (matches) {
                    // Replace URLs in the text node with anchor tags
                    matches.forEach(match => {
                        if (node.textContent) {
                            const link = doc.createElement("a");
                            link.href = match;
                            link.textContent = match;
                            link.target = '_blank';
                            node.replaceWith(link);
                        }
                    });
                }
            }
        } else {
            // If the node is not a text node, search its children
            if (node.nodeName !== "A") {
                node.childNodes.forEach(searchNode);
            }
        }
    }

    doc.body.childNodes.forEach(searchNode);

    return doc.body.innerHTML;
}

export function hexToRgbA(hex: string, alpha: number): string {
    let r: number = parseInt(hex.slice(1, 3), 16);
    let g: number = parseInt(hex.slice(3, 5), 16);
    let b: number = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}


export function formatStringOrDate(date: string | Date, formatString: string) {
    if (typeof date === "string") {
        return format(parseISO(date), formatString);
    }
    return format(date, formatString);
}

export function simpleHash(str: string) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36); // Convert to alphanumeric string
}

export function hashCurrentDate() {
    const now = new Date();
    const dateString = now.toJSON(); // Convert date to ISO string
    return simpleHash(dateString);
}


export default function invariant(
  cond?: boolean,
  message?: string,
  ..._: string[]
): asserts cond {
  if (cond) {
    return;
  }

  throw new Error(
    'Internal Lexical error: invariant() is meant to be replaced at compile ' +
      'time. There is no runtime version. Error: ' +
      message,
  );
}


export const formatSeconds = (seconds: number) => {
    const date = new Date(seconds * 1000);
    if (seconds > 3600) {
        return format(date, 'hh:mm');
    } else {
        return format(date, 'mm:ss');
    }
};


export function generateRandomString(length: number) {
  let result = "";
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}